import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalOrderFormData } from "@/lib/portal.functions";
import { listCreditMemos, getCreditMemo } from "@/lib/credit-memos.functions";
import { runServerFn } from "@/lib/server-fn";
import { useState } from "react";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/credits")({ component: PortalCredits });

const STATUS_VARIANT: Record<string, "secondary"|"default"|"outline"|"destructive"> = {
  draft: "secondary", issued: "default", applied: "outline", void: "destructive",
};

function PortalCredits() {
  const fetchForm = useServerFn(getPortalOrderFormData);
  const listFn = useServerFn(listCreditMemos);
  const getFn = useServerFn(getCreditMemo);

  const { data: form } = useQuery({
    queryKey: ["portal-form"], queryFn: () => runServerFn(() => fetchForm(), "Loading"),
  });
  const tenantId = form?.customer?.tenant_id;
  const customerId = form?.customer?.id;

  const { data } = useQuery({
    queryKey: ["portal-credit-memos", tenantId, customerId],
    enabled: !!tenantId && !!customerId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, customerId, status: "all" } }),
  });

  const [viewing, setViewing] = useState<string | null>(null);
  const { data: detail } = useQuery({
    queryKey: ["portal-credit-memo", viewing],
    enabled: !!viewing,
    queryFn: () => getFn({ data: { id: viewing! } }),
  });

  return (
    <>
      <PortalPageHeader title="Credit Memos" description="Credits issued for returns and adjustments." />
      <div className="p-4 sm:p-6 space-y-4">
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Memo #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Applied</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.memos ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground"><FileText className="h-4 w-4 inline mr-2" />No credit memos.</TableCell></TableRow>}
                {(data?.memos ?? []).map((m: any) => (
                  <TableRow key={m.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setViewing(m.id)}>
                    <TableCell className="font-medium">{m.memo_number}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[m.status]}>{m.status}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">${(m.total_cents / 100).toFixed(2)} {m.currency}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.issued_at ? new Date(m.issued_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.applied_at ? new Date(m.applied_at).toLocaleDateString() : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{detail?.memo?.memo_number ?? "Memo"}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Badge variant={STATUS_VARIANT[detail.memo.status]}>{detail.memo.status}</Badge>
                  <span className="ml-auto tabular-nums font-medium">${(detail.memo.total_cents / 100).toFixed(2)} {detail.memo.currency}</span>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {detail.lines.map((l: any) => (
                      <TableRow key={l.id}>
                        <TableCell>{l.description}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty}</TableCell>
                        <TableCell className="text-right tabular-nums">${(l.unit_price_cents / 100).toFixed(2)}</TableCell>
                        <TableCell className="text-right tabular-nums">${(l.amount_cents / 100).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {detail.memo.notes && <div className="text-xs text-muted-foreground">{detail.memo.notes}</div>}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}